/** Academy settings: plans sold (catalogue package → days, EO credits), Express Entry gift, filters. */

import { type AcademySettings, useAcademySettingsList, useAcademySettingsPartialUpdate, useCatalogPackagesList } from "@agencyos/api-client";
import { Button, Checkbox, GlassCard, SelectField, Skeleton, SoftToggle, TextField } from "@agencyos/ui-web";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";
import { ActivityReportsSettings } from "./ActivityReportsSettings";
import { EXAMS, useAdminExam } from "./examContext";
import { decimalInput, digits, ErrorList, isObject, nextKey, num, str, useAcademyRoles, useRefreshAcademy } from "./shared";

interface PlanRow {
  key: number;
  code: string;
  days: string;
  credits: string;
  /** Exams opened by the plan (D-105); none means the TCF, as before. */
  exams?: string[];
}

/** A plan without an exam list opens the TCF (server rule, D-105). */
export function planCovers(row: PlanRow, exam: string): boolean {
  return row.exams?.length ? row.exams.includes(exam) : exam === "tcf_canada";
}

export function plansOf(value: unknown): PlanRow[] {
  if (!isObject(value)) return [];
  return Object.entries(value).map(([code, plan]) => ({
    key: nextKey(),
    code,
    days: isObject(plan) ? String(num(plan.days)) : "",
    credits: isObject(plan) ? String(num(plan.eo_credits)) : "",
    exams: isObject(plan) && Array.isArray(plan.exams) ? (plan.exams as unknown[]).map(str).filter(Boolean) : [],
  }));
}

export function plansPayload(rows: PlanRow[]): Record<string, { days: number; eo_credits: number; exams?: string[] }> {
  return Object.fromEntries(
    rows
      .filter((r) => r.code.trim())
      .map((r) => [r.code.trim(), { days: Number(r.days) || 0, eo_credits: Number(r.credits) || 0, ...(r.exams?.length ? { exams: r.exams } : {}) }]),
  );
}

function SettingsForm({ row }: { row: AcademySettings }) {
  const { t, i18n } = useTranslation();
  const { canEditSettings } = useAcademyRoles();
  const { exam, ielts } = useAdminExam();
  const refresh = useRefreshAcademy();
  const save = useAcademySettingsPartialUpdate({ mutation: { onSuccess: refresh } });
  const packages = useCatalogPackagesList();
  const [plans, setPlans] = useState<PlanRow[]>(() => plansOf(row.plans));
  const [free, setFree] = useState(row.free_for_express_entry ?? true);
  const [threshold, setThreshold] = useState(String(row.plagiarism_threshold ?? 0.6));
  const [reminder, setReminder] = useState(String(row.reminder_days ?? 3));
  const errors = fieldErrors(save.error);
  const catalogue = packages.data?.status === 200 ? packages.data.data.results : [];
  const thresholdValue = Number(threshold);
  const thresholdError = threshold === "" || Number.isNaN(thresholdValue) || thresholdValue < 0 || thresholdValue > 1 ? t("academyAdmin.settings.thresholdRange") : undefined;
  const codes = plans.map((p) => p.code);
  const duplicate = codes.some((c, i) => c && codes.indexOf(c) !== i);

  const setPlan = (key: number, field: "code" | "days" | "credits", value: string) =>
    setPlans((all) => all.map((p) => (p.key === key ? { ...p, [field]: value } : p)));
  const toggleExam = (key: number, value: string, on: boolean) =>
    setPlans((all) =>
      all.map((p) => {
        if (p.key !== key) return p;
        const current = p.exams?.length ? p.exams : ["tcf_canada"];
        const next = on ? [...new Set([...current, value])] : current.filter((e) => e !== value);
        // Only the TCF: no list (the server's default), like the plans written before D-105.
        return { ...p, exams: next.length === 1 && next[0] === "tcf_canada" ? [] : next };
      }),
    );
  // The rows shown are those of the exam chosen at the top; the others are kept and saved as is.
  const shown = plans.filter((p) => planCovers(p, exam));
  const packageOptions = (current: string) => {
    const options = catalogue.map((p) => ({ value: p.code, label: `${p.code} — ${localized(p.name, i18n.language)}` }));
    if (current && !options.some((o) => o.value === current)) options.unshift({ value: current, label: current });
    return [{ value: "", label: packages.isPending ? t("common.loading") : t("academyAdmin.settings.pickPackage") }, ...options];
  };

  const onSave = () => {
    if (thresholdError || duplicate) return;
    save.mutate({
      id: row.id,
      data: {
        plans: plansPayload(plans),
        free_for_express_entry: free,
        plagiarism_threshold: thresholdValue,
        reminder_days: Number(reminder) || 0,
      },
    });
  };

  return (
    <GlassCard as="section" className="stack">
      {canEditSettings ? null : (
        <p role="note" className="muted">
          {t("common.readOnly")}
        </p>
      )}
      <fieldset className="plain-fieldset stack" disabled={!canEditSettings}>
        <h2>{t("academyAdmin.settings.plans")}</h2>
        <p className="muted">{t("academyAdmin.settings.plansHint")}</p>
        {ielts ? <p className="muted">{t("academyAdmin.ielts.settings.plansHint")}</p> : null}
        {shown.length ? null : <p className="muted">{ielts ? t("academyAdmin.ielts.settings.noPlans") : t("academyAdmin.settings.noPlans")}</p>}
        <ol className="plain-list stack">
          {shown.map((p) => (
            <li key={p.key} className="installment-row">
              <div className="form-grid">
                <SelectField label={t("academyAdmin.settings.package")} value={p.code} options={packageOptions(p.code)} onChange={(e) => setPlan(p.key, "code", e.target.value)} />
                <TextField label={t("academyAdmin.settings.days")} inputMode="numeric" value={p.days} onChange={(e) => setPlan(p.key, "days", digits(e.target.value))} />
                <TextField label={t("academyAdmin.settings.credits")} inputMode="numeric" value={p.credits} onChange={(e) => setPlan(p.key, "credits", digits(e.target.value))} />
              </div>
              <fieldset className="plain-fieldset row">
                <legend className="ao-footnote ao-secondary">{t("academyAdmin.ielts.settings.exams")}</legend>
                {EXAMS.map((e) => (
                  <Checkbox key={e} label={t(`academyAdmin.ielts.exam.${e}`)} checked={planCovers(p, e)} onChange={(event) => toggleExam(p.key, e, event.target.checked)} />
                ))}
              </fieldset>
              <div className="row">
                <Button variant="ghost" aria-label={t("common.remove")} title={t("common.remove")} onClick={() => setPlans((all) => all.filter((x) => x.key !== p.key))}>
                  <Trash2 size={16} aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}
        </ol>
        {duplicate ? (
          <p role="alert" className="ao-field__error">
            {t("academyAdmin.settings.duplicate")}
          </p>
        ) : null}
        {errors.plans ? (
          <p role="alert" className="ao-field__error">
            {errors.plans}
          </p>
        ) : null}
        <div className="row">
          <Button
            variant="secondary"
            icon={<Plus size={16} aria-hidden="true" />}
            onClick={() => setPlans((all) => [...all, { key: nextKey(), code: "", days: "30", credits: "0", exams: ielts ? [exam] : [] }])}
          >
            {t("academyAdmin.settings.addPlan")}
          </Button>
        </div>

        <h2>{t("academyAdmin.settings.access")}</h2>
        <SoftToggle label={t("academyAdmin.settings.free")} checked={free} disabled={!canEditSettings} onChange={setFree} />
        <p className="muted">{t("academyAdmin.settings.freeHint")}</p>
        <div className="form-grid">
          <TextField
            label={t("academyAdmin.settings.threshold")}
            hint={t("academyAdmin.settings.thresholdHint")}
            inputMode="decimal"
            value={threshold}
            error={thresholdError ?? errors.plagiarism_threshold}
            onChange={(e) => setThreshold(decimalInput(e.target.value))}
          />
          <TextField
            label={t("academyAdmin.settings.reminder")}
            hint={t("academyAdmin.settings.reminderHint")}
            inputMode="numeric"
            value={reminder}
            error={errors.reminder_days}
            onChange={(e) => setReminder(digits(e.target.value))}
          />
        </div>
        {save.error && !errors.plans && !errors.plagiarism_threshold && !errors.reminder_days ? <ErrorList error={save.error} /> : null}
        <div className="row">
          <Button disabled={save.isPending || Boolean(thresholdError) || duplicate} onClick={onSave}>
            {save.isPending ? t("common.loading") : t("common.save")}
          </Button>
          {save.isSuccess ? (
            <span role="status" className="ok">
              {t("common.saved")}
            </span>
          ) : null}
        </div>
      </fieldset>
    </GlassCard>
  );
}

export function SettingsTab() {
  const list = useAcademySettingsList();
  const row = list.data?.status === 200 ? list.data.data.results[0] : undefined;
  return row ? (
    <div className="stack">
      <SettingsForm key={row.id} row={row} />
      <ActivityReportsSettings key={`reports-${row.id}`} row={row} />
    </div>
  ) : (
    <Skeleton style={{ height: 240 }} />
  );
}
